import React, { useEffect, useRef, useMemo, Fragment, useState } from 'react';
import { observer } from 'mobx-react-lite';
import ReactMarkdown from 'react-markdown';
import { goSearchHistory, newSearch } from '@src/pages/search';
import globalStore from '@/store/global';
import { debounce } from 'lodash-es';
import { SVGPureIcon } from '@/ui';
import appLogger from '@public/log/monitor';
import inputTypeStore from '../input-file/input-type';
import './index.scss';
import { HelperCore } from '@/common/dependencies';
import navbarStore from '@/store/navbar';
import CircleLoading from '@/components/ani/circle-loading';
import Loader from '@/common/helper/loader';
import { useParams } from 'react-router-dom';
import chatStoreManager from '@/store/chat/manager';
import userStore from '@src/store/user';
import { Dropdown, Tooltip } from 'antd';
import slsLogger from '@public/log/sls';
import IconButton from '../chat-history/IconButton';
import deleteMenuStore from './delete-mune/store';
import { HelpOther } from '@/common/helper';
import { sendBroadcast, useBroadcast } from '@/hooks/useBroadcast';
import { request } from '@/common';
import { initSDK, checkChatExist, deleteChat } from '@q/nami-ai-kb-manager/ts'
import EE from '@/common/event';

let requesting = false;
const RenameInput = ({ item, setShowRenameInput }) => {
  const [text, setText] = useState(item.alias_title || item.title);
  const ref = useRef(null);

  const doEdit = () => {
    const conversation_id = item.conversation_id;
    const alias_name = ref.current.value;
    if (!alias_name.trim()) return;
    if (requesting) return;
    requesting = true;
    let params = {
      alias_name
    }
    if(item?.data_source === 2) {
      params.conversation_id = conversation_id
    } else {
      params.aiso_conversation_id = conversation_id
    }
    request
      .post('/conversation/edit/alias', {
        ...params
      })
      .then(res => {
        if (res.code == 0) {
          setShowRenameInput(false);
          navbarStore.updateAliasTitle(conversation_id, alias_name);
          sendBroadcast('history-changed');
        } else {
          HelpOther.toast('修改失败', 'error');
        }
        requesting = false;
      })
      .catch(() => {
        requesting = false;
        HelpOther.toast('修改失败', 'error');
      });
  };

  const handleKeyDown = event => {
    //回车提交
    if (event.keyCode == 13) {
      doEdit();
    }
    //ESC退出编辑模式
    if (event.keyCode == 27) {
      setShowRenameInput(false);
    }
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [ref]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="relative">
      <input
        ref={ref}
        value={text}
        onChange={evt => {
          setText(evt.target.value);
        }}
        onBlur={evt => {
          doEdit();
          setShowRenameInput(false);
        }}
        className="h-40px w-full text-14px text-black leading-40px border-1px border-black rounded-12px px-12px"
      />
    </div>
  );
};

const SO_WORD_LENGTH = 11;
const SUPER_WORLD_LENGTH = 9;

const AisoItem = observer(({ item, isSearch }) => {
  const {
    data_source = 1,
    conversation_id,
    alias_title,
    title,
    aiso_img_search,
    // 超级搜索取这个字段
    img_search,
    icon,
    content,
    // 超级搜索取这个字段
    summary_content,
  } = item;

  const params = useParams();
  const id = params?.id || params?.search_id;
  const chatStore = chatStoreManager.get(conversation_id);
  const loading = chatStore?.isAITyping;

  const isCur = item.conversation_id == (id || navbarStore.curConversationId);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRenameInput, setShowRenameInput] = useState(false);

  const selected = (id || navbarStore.curConversationId) === conversation_id;
  const image_url = data_source === 1 ? (aiso_img_search?.length > 0 ? aiso_img_search[0] : icon) : (img_search?.length > 0 ? img_search[0] : icon);
  const newContent = data_source === 1 ? content : summary_content;
  const maxLength = data_source === 1 ? SO_WORD_LENGTH : SUPER_WORLD_LENGTH;
  const header = alias_title || title || '';

  const handleItemClick = item => {
    inputTypeStore.reset();
    navbarStore.setCurConversationId(conversation_id);

    appLogger.log({
      key: 'aiso_home',
      action: 'click',
      arrt: 'aiso_history',
      ext: appLogger.getSubContent(header),
      conversation_id: conversation_id,
    });

    if (data_source === 1) {
      goSearchHistory(conversation_id);
    } else if (data_source === 2) {
      globalStore.navigate(`/aichat/${conversation_id}`);
    }
  };

  // 使用shadow dom来渲染内容，防止内容溢出
  const shadowRootRef = useRef(null);
  useEffect(() => {
    if (shadowRootRef.current && !shadowRootRef.current.shadowRoot) {
      // 创建 Shadow DOM
      const shadowRoot = shadowRootRef.current.attachShadow({ mode: 'open' });
      shadowRoot.innerHTML = HelperCore.xss(newContent);
    }
    return () => {
      if (shadowRootRef.current && !shadowRootRef.current.shadowRoot) {
        shadowRootRef.current.innerHTML = ''; // 清空内容
      }
    };
  }, [newContent])

  const dropList = (item, isCur, setShowDropdown, setShowRenameInput) => {
    const conversation_id = item.conversation_id;
    const isTopUp = item.pin_top_time; //有pin_top_time字段就认为是置顶了
    const action = isTopUp ? '2' : '1'; //1 置顶操作 2 取消置顶
    return (
      <div
        className={`w-[150px] px-[8px] py-[8px] rounded-[12px] flex flex-col ${globalStore.isDark ? 'bg-[#31313A]' : 'bg-white'}`}
        style={{
          boxShadow: '0px 2px 8px 0px #0000001A',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <div
          className={`w-full h-[40px] rounded-[8px] font-bold text-[14px] text-[#000] flex items-center cursor-pointer hover:bg-[#000] hover:bg-opacity-4 ${globalStore.isDark ? 'bg-[#31313A]' : 'bg-white'}`}
          onClick={evt => {
            const params = {
              conversation_id,
              action,
              data_source: data_source,
            };
            slsLogger.log({
              key : "chat",
              action : "pinTopConversation",
              ext : JSON.stringify(params),
            });
            request
              .post('/conversation/edit/pin_top', params)
              .then(res => {
                if (res.code == 0) {
                  navbarStore.updateTopUp(
                    conversation_id,
                    isTopUp ? '' : +new Date()
                  );
                  sendBroadcast('history-changed');
                }
              })
              .catch(Loader.error);
            setShowDropdown(false);
            evt.stopPropagation();
          }}
        >
          <IconButton
            className="!hover:bg-transparent"
            icon={isTopUp ? 'topup_cancel' : 'topup'}
            size={20}
          ></IconButton>
          {isTopUp ? '取消置顶' : '置顶任务'}
        </div>
        <div
          className={`w-full h-[40px] rounded-[8px] font-bold text-[14px] text-[#000] flex items-center cursor-pointer hover:bg-[#000] hover:bg-opacity-4 ${globalStore.isDark ? 'bg-[#31313A]' : 'bg-white'}`}
          onClick={evt => {
            setShowRenameInput(true);
            setShowDropdown(false);
            evt.stopPropagation();
          }}
        >
          <IconButton
            className="!hover:bg-transparent"
            icon="edit"
            size={20}
          ></IconButton>
          重命名
        </div>
        <div
          className={`w-full h-[40px] rounded-[8px] font-bold text-[14px] text-[#FF3B30] flex items-center cursor-pointer hover:bg-[#FEF5F6] ${globalStore.isDark ? 'bg-[#31313A]' : 'bg-white'}`}
          onClick={evt => {
            setShowDropdown(false);
            HelpOther.confirm({
              title: '确认删除任务',
              okText: '删除',
              content: (
                <div className="whitespace-nowrap">
                  确定要删除任务吗？删除后任务内容将不可恢复。
                </div>
              ),
              onOk: () => {
                if (isCur) {
                  if (chatStoreManager.current?.isAITyping) {
                    chatStoreManager.current.abort(() => {
                      navbarStore.clearHistory(item.conversation_id, true);
                    });
                  } else {
                    navbarStore.clearHistory(item.conversation_id, true);
                  }
                } else {
                  navbarStore.clearHistory(item.conversation_id);
                }
                appLogger.log({
                  key: 'aibot_home',
                  action: 'click',
                  arrt: 'del_chat',
                  conversation_id: item?.conversation_id,
                });
              },
            });
            evt.stopPropagation();
          }}
        >
          <IconButton
            className="!hover:bg-transparent"
            icon="icon_delete_dropdown"
            size={20}
          ></IconButton>
          删除任务
        </div>
      </div>
    );
  };

  useEffect(()=>{
    // 选中的,将标签页title改为header
    if(selected) {
      globalStore.setDocumentInfo({ title : header })
    }
  }, [header, selected])

  return (
    <div
      className={[
        'relative flex justify-between w-full group',
        'p-12px bg-[#fff] cursor-pointer',
        'rounded-6px overflow-hidden',
        // 'hover:border-1 hover:border-[rgba(0,0,0,0.1)]',
        globalStore.isDark
          ? '!hover:bg-[#3D3D46] group'
          : '!hover:shadow-md group',
        selected
          ? 'border-[1px] border-[#000]'
          : 'border-[1px] border-[rgba(0,0,0,0)]',
      ]}
      style={{
        boxShadow: selected ? "0 0 2px 0 rgba(0, 0, 0, 0.12), 0 2px 4px 0 rgba(0, 0, 0, 0.14)" : ""
      }}
      onClick={evt => {
        debounce(() => {
          handleItemClick(item);
        }, 300)();
      }}
    >
      <div className="flex flex-col gap-4px overflow-hidden navbar-search-item justify-center">
      <div className='flex items-start gap-6px relative'>
        {data_source === 2 && !showRenameInput && <div className='w-18px h-18px rounded-4px bg-[#e5f0ff] flex items-center justify-center absolute top-0 left-0'><SVGPureIcon name="super-search" size={14} color={'#006AFF'} /></div>}
        {showRenameInput ? <RenameInput item={item} setShowRenameInput={setShowRenameInput} /> : <div
            className={[
              'w-full text-[#000] text-14px leading-18px break-all line-clamp-2 relative',
              `${data_source === 2 ? 'indent-24px' : ''}`,
            ]}
            dangerouslySetInnerHTML={{
              __html: header,
            }}
          >
            {/* {header} */}
          </div>}
      </div>
        {loading ? (
          <div className="flex text-12px h-24px text-[rgba(0,0,0,0.5)] bg-[#EAEFF9] rounded-6px mt-2px py-6px pl-0px pr-8px items-center w-min whitespace-nowrap">
            {/* <CircleLoading className="!size-16px ml-6px flex-shrink-0" icon={Loader.resources.loading.blue} /> */}
            <CircleLoading
              className="!size-12px ml-6px flex-shrink-0"
              icon={'https://s0.ssl.qhres2.com/static/644a2539f476e8bc.svg'}
            />
            <span className="text-[#0058ff] ml-4px">任务进行中</span>
          </div>
        ) : data_source === 1 ? (
          <div
            className={[
              'text-12px text-[rgba(0,0,0,0.5)] mt-4px leading-17px',
              header.length > maxLength ? 'line-clamp-1' : 'line-clamp-2',
              !content ? '!m-0px' : '',
            ]}
            ref={shadowRootRef}
          ></div>
        ) : (
          <div
            className={[
              'text-12px text-[rgba(0,0,0,0.5)] mt-4px leading-17px',
              header.length > maxLength ? 'line-clamp-1' : 'line-clamp-2',
              !content ? '!m-0px' : '',
            ]}
          >
            <ReactMarkdown>{newContent}</ReactMarkdown>
          </div>
        )}
        {/* <div className="text-12px text-[rgba(0,0,0,0.5)] leading-17px">@ {data.creator}</div> */}
      </div>
      {image_url && (
        <img
          className="w-36px h-36px object-cover rounded-4px ml-8px"
          src={image_url}
        />
      )}
      {userStore.isUser && !isSearch && <div
            className={[
              'w-26px h-26px rounded-[6px] flex items-end justify-end hidden absolute bottom-0 right-0px bottom-0 group-hover:flex mr-12px mb-7px',
              showDropdown ? '!flex' : '',
              deleteMenuStore.isEdit ||
              item.conversation_id == chatStoreManager.getVirtualId()
                ? '!hidden'
                : '',
            ]}
            style={{
                background: 'white',
            }}
          >
            <Dropdown
              dropdownRender={() =>
                dropList(item, isCur, setShowDropdown, setShowRenameInput)
              }
              trigger="click"
              placement="bottomLeft"
              open={showDropdown}
              onOpenChange={visible => {
                setShowDropdown(visible);
              }}
            >
              <IconButton
                className={[
                  '!hover:bg-[rgba(0,0,0,0.04)]',
                  'border border-black/10 shadow-sm',
                  'bg-[rgb(255,255,255,1)] !p-0',
                  'rounded-[6px]',
                  showDropdown ? '!bg-[rgba(0,0,0,0.04)]' : '',
                ]}
                iconClassName="text-[rgb(0,0,0,.5)]"
                icon="more"
                size={24}
                onClick={evt => {
                  evt.stopPropagation();
                }}
              />
            </Dropdown>
      </div>}
    </div>
  );
});

export default AisoItem;